import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { authApi, menuPermissionsApi } from '../../services/api'

const RUOLI = [
  'superadmin', 'admin', 'dispatcher', 'responsabile_tecnico', 'tecnico_senior',
  'tecnico', 'contabilita', 'committente', 'cliente_finale', 'readonly',
]

interface Richiesta {
  id: number; email: string; nome: string; cognome: string
  telefono_whatsapp?: string; stato: string; token: string; created_at: string
}
interface Lockout {
  id: number; email: string; tentativi_falliti: number; bloccato: boolean
  bloccato_at?: string; utente_esiste: boolean; nome?: string; cognome?: string
}
interface ResetRichiesta {
  id: number; email: string; stato: string; token: string; created_at: string
  utente_esiste: boolean; nome?: string; cognome?: string
}
interface TotpRichiesta {
  id: number; email: string; stato: string; token: string; created_at: string
  nome?: string; cognome?: string
}
interface MenuDef { id: string; label: string; category: string }

export default function RichiesteEBlocchi() {
  const [searchParams] = useSearchParams()
  const [richieste, setRichieste] = useState<Richiesta[]>([])
  const [lockouts, setLockouts] = useState<Lockout[]>([])
  const [resetRichieste, setResetRichieste] = useState<ResetRichiesta[]>([])
  const [totpRichieste, setTotpRichieste] = useState<TotpRichiesta[]>([])
  const [menus, setMenus] = useState<MenuDef[]>([])
  const [approvazioneAperta, setApprovazioneAperta] = useState<Richiesta | null>(null)
  const [ruoloScelto, setRuoloScelto] = useState('tecnico')
  const [permessiScelti, setPermessiScelti] = useState<Record<string, boolean>>({})
  const [emailEvidenziata, setEmailEvidenziata] = useState<string | null>(null)
  const [tokenResetEvidenziato, setTokenResetEvidenziato] = useState<string | null>(null)
  const [tokenTotpEvidenziato, setTokenTotpEvidenziato] = useState<string | null>(null)

  const carica = () => {
    authApi.registrazioni.lista().then(r => setRichieste(r.data)).catch(() => {})
    authApi.lockouts.lista().then(r => setLockouts(r.data)).catch(() => {})
    authApi.resetPasswordRichieste.lista().then(r => setResetRichieste(r.data)).catch(() => {})
    authApi.totpResetRichieste.lista().then(r => setTotpRichieste(r.data)).catch(() => {})
    menuPermissionsApi.allMenus().then((r: any) => setMenus(r.data)).catch(() => {})
  }

  useEffect(carica, [])

  useEffect(() => {
    const tokenRichiesta = searchParams.get('richiesta')
    if (tokenRichiesta && richieste.length > 0) {
      const trovata = richieste.find(r => r.token === tokenRichiesta)
      if (trovata) apriApprovazione(trovata)
    }
  }, [searchParams, richieste])

  useEffect(() => {
    const emailBloccati = searchParams.get('bloccati')
    if (emailBloccati) setEmailEvidenziata(emailBloccati)
  }, [searchParams])

  useEffect(() => {
    const tokenReset = searchParams.get('reset')
    if (tokenReset) setTokenResetEvidenziato(tokenReset)
  }, [searchParams])

  useEffect(() => {
    const tokenTotp = searchParams.get('totp_reset')
    if (tokenTotp) setTokenTotpEvidenziato(tokenTotp)
  }, [searchParams])

  const approvaTotp = async (r: TotpRichiesta) => {
    try {
      await authApi.totpResetRichieste.approva(r.token)
      toast.success('2FA sbloccata per l\'utente')
      carica()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Errore approvazione')
    }
  }

  const rifiutaTotp = async (r: TotpRichiesta) => {
    if (!confirm(`Rifiutare lo sblocco 2FA per ${r.email}?`)) return
    try {
      await authApi.totpResetRichieste.rifiuta(r.token)
      toast.success('Richiesta rifiutata')
      carica()
    } catch {
      toast.error('Errore rifiuto')
    }
  }

  const approvaReset = async (r: ResetRichiesta) => {
    try {
      await authApi.resetPasswordRichieste.approva(r.token)
      toast.success(r.utente_esiste ? 'Password inviata via email' : 'Autorizzato (nessun utente corrispondente)')
      carica()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Errore approvazione')
    }
  }

  const rifiutaReset = async (r: ResetRichiesta) => {
    if (!confirm(`Rifiutare la richiesta di reset password per ${r.email}?`)) return
    try {
      await authApi.resetPasswordRichieste.rifiuta(r.token)
      toast.success('Richiesta rifiutata')
      carica()
    } catch {
      toast.error('Errore rifiuto')
    }
  }

  const apriApprovazione = (r: Richiesta) => {
    setApprovazioneAperta(r)
    setRuoloScelto('tecnico')
    setPermessiScelti({})
  }

  const approva = async () => {
    if (!approvazioneAperta) return
    try {
      await authApi.registrazioni.approva(approvazioneAperta.token, {
        role: ruoloScelto,
        permessi_menu: Object.entries(permessiScelti).map(([menu_id, enabled]) => ({ menu_id, enabled })),
      })
      toast.success('Utente creato e credenziali inviate')
      setApprovazioneAperta(null)
      carica()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Errore approvazione')
    }
  }

  const rifiuta = async (r: Richiesta) => {
    if (!confirm(`Rifiutare la richiesta di ${r.nome} ${r.cognome}?`)) return
    try {
      await authApi.registrazioni.rifiuta(r.token)
      toast.success('Richiesta rifiutata')
      carica()
    } catch {
      toast.error('Errore rifiuto')
    }
  }

  const sblocca = async (email: string) => {
    try {
      await authApi.lockouts.sblocca(email)
      toast.success('Utenza sbloccata')
      carica()
    } catch {
      toast.error('Errore sblocco')
    }
  }

  return (
    <div style={{ padding: 16 }}>
      <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>Richieste di registrazione in attesa</h3>
      {richieste.length === 0 && <div style={{ color: '#9CA3AF', fontSize: 13, marginBottom: 20 }}>Nessuna richiesta in attesa</div>}
      {richieste.map(r => (
        <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
          <div style={{ flex: 1, fontSize: 13 }}>
            <strong>{r.nome} {r.cognome}</strong> — {r.email} {r.telefono_whatsapp && `— ${r.telefono_whatsapp}`}
          </div>
          <button className="btn-primary" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => apriApprovazione(r)}>Approva</button>
          <button className="btn-secondary" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => rifiuta(r)}>Rifiuta</button>
        </div>
      ))}

      <h3 style={{ fontSize: 15, fontWeight: 700, margin: '24px 0 8px' }}>Utenze bloccate</h3>
      {lockouts.length === 0 && <div style={{ color: '#9CA3AF', fontSize: 13 }}>Nessuna utenza bloccata</div>}
      {lockouts.map(l => (
        <div
          key={l.id}
          style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0',
            borderBottom: '1px solid #F3F4F6',
            background: emailEvidenziata === l.email ? '#FEF3C7' : 'transparent',
          }}
        >
          <div style={{ flex: 1, fontSize: 13 }}>
            <strong>{l.email}</strong>
            {l.utente_esiste ? ` — ${l.nome} ${l.cognome}` : ' — nessun utente esistente'}
            {' — '}{l.tentativi_falliti} tentativi falliti
          </div>
          <button className="btn-primary" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => sblocca(l.email)}>Sblocca</button>
        </div>
      ))}

      <h3 style={{ fontSize: 15, fontWeight: 700, margin: '24px 0 8px' }}>Richieste sblocco 2FA (app authenticator persa)</h3>
      {totpRichieste.length === 0 && <div style={{ color: '#9CA3AF', fontSize: 13 }}>Nessuna richiesta in attesa</div>}
      {totpRichieste.map(r => (
        <div
          key={r.id}
          style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0',
            borderBottom: '1px solid #F3F4F6',
            background: tokenTotpEvidenziato === r.token ? '#FEF3C7' : 'transparent',
          }}
        >
          <div style={{ flex: 1, fontSize: 13 }}>
            <strong>{r.email}</strong>{r.nome && ` — ${r.nome} ${r.cognome}`}
          </div>
          <button className="btn-primary" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => approvaTotp(r)}>Sblocca 2FA</button>
          <button className="btn-secondary" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => rifiutaTotp(r)}>Rifiuta</button>
        </div>
      ))}

      <h3 style={{ fontSize: 15, fontWeight: 700, margin: '24px 0 8px' }}>Richieste di reset password</h3>
      {resetRichieste.length === 0 && <div style={{ color: '#9CA3AF', fontSize: 13 }}>Nessuna richiesta in attesa</div>}
      {resetRichieste.map(r => (
        <div
          key={r.id}
          style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0',
            borderBottom: '1px solid #F3F4F6',
            background: tokenResetEvidenziato === r.token ? '#FEF3C7' : 'transparent',
          }}
        >
          <div style={{ flex: 1, fontSize: 13 }}>
            <strong>{r.email}</strong>
            {r.utente_esiste ? ` — ${r.nome} ${r.cognome}` : ' — nessun utente esistente'}
          </div>
          <button className="btn-primary" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => approvaReset(r)}>Autorizza invio</button>
          <button className="btn-secondary" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => rifiutaReset(r)}>Rifiuta</button>
        </div>
      ))}

      {approvazioneAperta && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setApprovazioneAperta(null)}>
          <div className="card w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} style={{ padding: 20 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>
              Approva {approvazioneAperta.nome} {approvazioneAperta.cognome}
            </h3>
            <label className="label">Ruolo</label>
            <select className="select" value={ruoloScelto} onChange={e => setRuoloScelto(e.target.value)} style={{ marginBottom: 16 }}>
              {RUOLI.map(r => <option key={r} value={r}>{r}</option>)}
            </select>

            <label className="label">Permessi menu</label>
            <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 8, padding: 8, marginBottom: 16 }}>
              {menus.map(m => (
                <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '3px 0' }}>
                  <input
                    type="checkbox"
                    checked={!!permessiScelti[m.id]}
                    onChange={e => setPermessiScelti(prev => ({ ...prev, [m.id]: e.target.checked }))}
                  />
                  {m.label}
                </label>
              ))}
            </div>

            <div className="flex gap-3">
              <button className="btn-secondary flex-1" onClick={() => setApprovazioneAperta(null)}>Annulla</button>
              <button className="btn-primary flex-1" onClick={approva}>Approva e crea utente</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
